import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import caribbeanBg from "../../assets/img/caribbean-bg.webp";
import caribbeanLogo from "../../assets/img/caribbean-logo.webp";
import { DatePicker, DestinationSelect, PreLoading } from "@tripian/react";
import useCities from "../../hooks/useCities";
import useTranslate from "../../hooks/useTranslate";
import useAuth from "../../hooks/useAuth";
import useUser from "../../hooks/useUser";
import { saveLocalStorageToken } from "../../App/AppWrapper/localStorages";
import Model, { helper } from "@tripian/model";
import { api } from "@tripian/core";
import moment from "moment";
import { CREATE_TRIP, LOGIN, TRIPS } from "../../constants/ROUTER_PATH_TITLE";

export type RSelectOption = {
  id: number;
  label: string;
  payload: {
    destinationId: number;
    destinationName: string;
    coordinate: Model.Coordinate;
    parentName: string;
  };
  isSelected?: boolean;
};

const SimplyCaribbean = () => {
  const { cities, loadingCities } = useCities();
  const { t } = useTranslate();
  const { loggedIn, loadingIsLoggedIn, isLoggedIn } = useAuth();
  const { userFetch, loadingUser, user } = useUser();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const location = useLocation();

  const cityId = searchParams.get("cityId");
  const arrivalDate = searchParams.get("arrivalDate");
  const departureDate = searchParams.get("departureDate");

  const [tripProfile, setTripProfile] = useState<Model.TripProfile>({
    cityId: cityId ? parseInt(cityId) : 0,
    arrivalDatetime:
      arrivalDate ||
      moment()
        .add(1, "days")
        .set({ hour: 9, minute: 0 })
        .format("YYYY-MM-DDTHH:mm:ss[Z]"),
    departureDatetime:
      departureDate ||
      moment()
        .add(4, "days")
        .set({ hour: 18, minute: 0 })
        .format("YYYY-MM-DDTHH:mm:ss[Z]"),
    answers: [],
    numberOfAdults: 1,
    companionIds: [],
    doNotGenerate: 0,
    excludeHash: [],
  });

  useEffect(() => {
    const state = location.state as {
      cityId?: number;
      arrivalDate?: string;
      departureDate?: string;
    };
    if (state && state.cityId && state.arrivalDate && state.departureDate) {
      setTripProfile(prev => ({
        ...prev,
        cityId: state.cityId as number,
        arrivalDatetime: state.arrivalDate as string,
        departureDatetime: state.departureDate as string,
      }));
    }
  }, [location.state]);

  useEffect(() => {
    const performGuestLogin = async () => {
      if (!loadingUser && user && user.userType === Model.USER_TYPE.GUEST) {
        return;
      }

      try {
        const uniqueId = `simply-caribbean-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        const token = await api.loginGuest(uniqueId);
        saveLocalStorageToken(token);
        await userFetch(true);
        loggedIn();
        userFetch(true);
      } catch (error) {
        console.error("Guest login failed:", error);
      }
    };

    if (!loadingIsLoggedIn && !isLoggedIn) {
      performGuestLogin();
    }
  }, [isLoggedIn, loadingIsLoggedIn, loadingUser, loggedIn, user, userFetch]);

  const destinations: {
    destinationId: number;
    destinationName: string;
    coordinate: any;
    parentName: string;
  }[] = useMemo(() => {
    if (cities) {
      return cities?.map(city => {
        return {
          destinationId: city.id,
          destinationName: city.name,
          coordinate: city.coordinate,
          parentName:
            city.parentLocations.length === 0
              ? city.country.name
              : `${city.parentLocations.map(parent => parent.name).join(", ")}, ${city.country.name}`,
        };
      });
    }
    return [];
  }, [cities]);

  const destinationOptions: RSelectOption[] = useMemo(
    () =>
      destinations
        .sort((a, b) =>
          helper.compareStringForSort(a.destinationName, b.destinationName)
        )
        .map(d => ({
          id: d.destinationId,
          label: `${d.destinationName} - ${d.parentName}`,
          payload: d,
          isSelected: d.destinationId === tripProfile.cityId,
        })),
    [destinations, tripProfile.cityId]
  );

  const handleCitySelection = (selectedOption: RSelectOption) => {
    setTripProfile(prev => ({ ...prev, cityId: selectedOption.id }));
  };

  const handleStartDateChange = (date: moment.Moment | null) => {
    if (date) {
      const startDate = date.clone().startOf("day");
      const endDate = moment(tripProfile.departureDatetime)
        .clone()
        .startOf("day");

      const dayDiff = startDate.diff(endDate, "days");

      if (dayDiff >= 0) {
        setTripProfile(prev => ({
          ...prev,
          arrivalDatetime: startDate.format("YYYY-MM-DD"),
          departureDatetime: startDate.format("YYYY-MM-DD"),
        }));
      } else {
        setTripProfile(prev => ({
          ...prev,
          arrivalDatetime: startDate.format("YYYY-MM-DD"),
        }));
      }
    }
  };

  const handleEndDateChange = (date: moment.Moment | null) => {
    if (date) {
      const endDate = date.startOf("day");
      const currentStartDate = moment(tripProfile.arrivalDatetime).startOf(
        "day"
      );

      if (endDate.isAfter(currentStartDate)) {
        setTripProfile(prev => ({
          ...prev,
          departureDatetime: endDate.format("YYYY-MM-DD"),
        }));
      } else {
        setTripProfile(prev => ({
          ...prev,
          departureDatetime: currentStartDate
            .clone()
            .add(1, "day")
            .format("YYYY-MM-DD"),
        }));
      }
    }
  };

  const handleSubmit = () => {
    if (
      tripProfile.cityId &&
      tripProfile.arrivalDatetime &&
      tripProfile.departureDatetime
    ) {
      navigate(CREATE_TRIP.PATH, {
        state: {
          cityId: tripProfile.cityId,
          arrivalDate: tripProfile.arrivalDatetime,
          departureDate: tripProfile.departureDatetime,
          fromSimplyCaribbean: true,
        },
      });
    }
  };

  const handleClick = () => {
    if (user?.userType === Model.USER_TYPE.GUEST) {
      navigate(LOGIN.PATH);
    } else {
      navigate(TRIPS.PATH);
    }
  };

  return (
    <div className="w-full min-h-screen bg-white overflow-hidden">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Alice&family=Almarai:wght@300;400;700;800&family=Baskervville:ital@1&family=Geist:wght@100..900&family=Inter:ital,opsz,wght@0,14..32,100..900;1,14..32,100..900&family=Montserrat:ital,wght@0,100..900;1,100..900&family=Poppins:ital,wght@0,100;0,200;0,300;0,400;0,500;0,600;0,700;0,800;0,900;1,100;1,200;1,300;1,400;1,500;1,600;1,700;1,800;1,900&display=swap');
        .font-alice { font-family: 'Alice', serif; font-weight: 400; }
        .font-almarai { font-family: 'Almarai', sans-serif; font-weight: 400; }
        .font-baskervville { font-family: 'Baskervville', serif; }
        .font-geist { font-family: 'Geist', sans-serif; }
        .font-inter { font-family: 'Inter', sans-serif; }
        .font-montserrat { font-family: 'Montserrat', sans-serif; }
        .font-poppins { font-family: 'Poppins', sans-serif; }
      `}</style>

      <header className="h-[10vh] w-full bg-white flex items-center justify-between px-6 shadow-[0_0.125rem_0.1875rem_0_#0000004D] z-30">
        <img className="h-12 w-auto" src={caribbeanLogo} alt="Caribbean" />
        {user && user.userType === Model.USER_TYPE.GUEST && (
          <button
            className="bg-[#6B726F] hover:bg-[#697772] text-white font-semibold text-base px-7 py-3 rounded-full min-w-28"
            onClick={handleClick}
          >
            SIGN IN
          </button>
        )}
      </header>
      {loadingCities || loadingUser ? (
        <div className="flex items-center justify-center min-h-screen">
          <PreLoading />
        </div>
      ) : (
        <div className="relative w-[calc(100%-4vh)] rounded-3xl shadow-2xl overflow-hidden bg-white mt-[2vh] mb-[2vh] ml-[2vh] flex-1 flex justify-center flex-col h-auto md:h-[86vh]">
          <img
            src={caribbeanBg}
            alt="Caribbean Surf"
            className="absolute w-full h-full object-cover object-center z-0"
          />

          <div className="absolute w-full h-full bg-black bg-opacity-25 z-[1]" />

          <div className="relative z-[1] flex flex-col h-full justify-between overflow-y-auto">
            <div className="absolute md:left-1/2 right-4 top-4 md:top-0 md:-translate-x-[-75%] md:-translate-y-[-15%] z-[1] w-32 h-32 md:w-56 md:h-56 flex items-center justify-center border-2 border-white rounded-full">
              <div className="w-28 h-28 md:w-52 md:h-52 rounded-full flex flex-col items-center justify-center bg-white/80 pt-3 md:pt-6">
                <p className="text-center text-gray-700 font-inter font-light text-[0.675rem]/[1.125rem] md:text-[1.125rem] md:leading-[2rem] px-2 md:px-6">
                  Let's plan a trip packed with the best things to do!
                </p>
                <div className="mt-0">
                  <svg
                    className="w-6 h-6 md:w-16 md:h-16"
                    width="60"
                    height="56"
                    viewBox="0 0 60 56"
                    fill="none"
                  >
                    <path
                      d="M42.5 16.3333L17.5 39.6667"
                      stroke="#0B1215"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M42.5 39.6667H17.5V16.3333"
                      stroke="#0B1215"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
              </div>
            </div>

            <div className="relative flex flex-col justify-start items-start max-w-xl pl-4 pt-24 md:pl-12 md:pt-20">
              <h1 className="text-white font-alice font-normal text-[4rem] leading-[100%] mb-6 drop-shadow-lg md:block hidden">
                Best Things to Do in the Caribbean
              </h1>
              <h1 className="text-white font-alice font-normal text-3xl leading-[110%] mb-3 drop-shadow-lg block md:hidden">
                Best Things
                <br />
                to Do in the
                <br />
                Caribbean
              </h1>
              <p className="text-white font-almarai font-normal text-[0.9375rem] md:text-[1.0625rem] leading-[1.5rem] md:leading-[1.6875rem] text-left md:text-justify drop-shadow-md">
                The Caribbean is a vibrant region with 33 unique destinations
                all offering endless adventures and an incredible range of
                unique experiences. From Florida in the north to Columbia and
                Venezuela in the south, the choices are as diverse as they are
                exciting!
              </p>
            </div>

            <div className="w-full flex pb-8 pl-4 pr-4 md:pr-0 md:pl-12 mt-20 md:mt-0 opacity-80 md:opacity-100">
              <div className="relative w-full max-w-6xl bg-white shadow-2xl rounded-[3rem] flex flex-col md:flex-row items-center px-8 py-8 gap-4">
                <div className="absolute -top-14 h-28 left-0 flex items-center bg-white rounded-tl-[2.5rem] rounded-tr-[2.5rem] md:rounded-br-[2.5rem] rounded-bl-none pl-7 pr-24 pb-8 w-full md:w-fit">
                  <svg
                    className="mx-2"
                    width="1.5rem"
                    height="1.5rem"
                    viewBox="0 0 24 24"
                    fill="none"
                  >
                    <path
                      d="M18.3335 6.41666H3.66683C2.65431 6.41666 1.8335 7.23748 1.8335 8.25V17.4167C1.8335 18.4292 2.65431 19.25 3.66683 19.25H18.3335C19.346 19.25 20.1668 18.4292 20.1668 17.4167V8.25C20.1668 7.23748 19.346 6.41666 18.3335 6.41666Z"
                      stroke="#0B1215"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M14.6668 19.25V4.58333C14.6668 4.0971 14.4737 3.63079 14.1299 3.28697C13.786 2.94315 13.3197 2.75 12.8335 2.75H9.16683C8.6806 2.75 8.21428 2.94315 7.87047 3.28697C7.52665 3.63079 7.3335 4.0971 7.3335 4.58333V19.25"
                      stroke="#0B1215"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  <span className="font-bold text-base text-black">
                    Itinerary Planner
                  </span>
                </div>

                <div className="flex flex-col flex-1 min-w-full md:min-w-[12.5rem] z-[1] ">
                  <span className="font-semibold text-sm text-gray-900 mb-2 ml-2">
                    Where
                  </span>
                  {destinationOptions.length > 0 && (
                    <DestinationSelect
                      options={destinationOptions}
                      selectedOptionId={tripProfile.cityId}
                      onSelectedOptionChange={handleCitySelection}
                      placeHolder={t(
                        "trips.createNewTrip.form.destination.city.placeholder"
                      )}
                      t={t}
                    />
                  )}
                </div>

                <div className="flex flex-col min-w-full md:min-w-[12.5rem] z-[1]">
                  <span className="font-semibold text-sm text-gray-900 mb-2 ml-2">
                    Depart
                  </span>
                  <div className="relative z-50">
                    <DatePicker
                      currentDate={moment(tripProfile.arrivalDatetime)}
                      onchanged={handleStartDateChange}
                      isDayBlocked={(date: moment.Moment | null) => {
                        if (!date) return false;
                        return date.isBefore(moment().startOf("day"));
                      }}
                      openDirection="up"
                      showMonthSelection
                    />
                  </div>
                </div>

                <div className="flex flex-col min-w-full md:min-w-[12.5rem] z-[1]">
                  <span className="font-semibold text-sm text-gray-900 mb-2 ml-2">
                    Return
                  </span>
                  <DatePicker
                    currentDate={moment(tripProfile.departureDatetime)}
                    onchanged={handleEndDateChange}
                    isDayBlocked={(date: moment.Moment | null) => {
                      if (!date) return false;
                      const startDate = moment(
                        tripProfile.arrivalDatetime
                      ).startOf("day");
                      return date.isSameOrBefore(startDate);
                    }}
                    openDirection="up"
                    showMonthSelection
                  />
                </div>

                <div className="flex flex-col min-w-full md:min-w-[12.5rem] z-[1]">
                  <span className="invisible mb-2">Submit</span>
                  <button
                    className="bg-[#6B726F] hover:bg-[#697772] text-white font-semibold text-base px-7 py-3 rounded-full w-full"
                    onClick={handleSubmit}
                  >
                    Plan Your Trip Now
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SimplyCaribbean;
